import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { Sun, Moon, Sparkles, ChevronUp, HelpCircle } from 'lucide-react'
import { useTheme } from '../../contexts/ThemeContext'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useNavVisibility } from '../../hooks/useNavVisibility'
import { useAppMode } from '../../contexts/AppModeContext'
import {
  useBranding,
  useBrandName,
  useBrandedText,
} from '../../contexts/UiCustomizationContext'
import { config } from '../../config'
import { formatAppVersionTitle } from '../../utils/appVersion'
import type { AppMode } from '../../contexts/AppModeContext'
import { NAV_SECTIONS, MILKYWAY_NAV_SECTIONS, DATAFLOW_NAV_SECTIONS, type NavSection, type NavItem } from '../../constants/navigation'
import { AquaLogo } from '../../assets/icons/AquaLogo'
import { OptimisticNavLink as NavLink } from '../routing/OptimisticNavigation'
import { useRoutePrefetch } from '../../hooks/useRoutePrefetch'
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext'
import { FeatureStageBadge } from '../FeatureStageBadge'

import './AppSidebar.css'

function getNavScrollMetrics(nav: HTMLElement) {
  const { scrollTop, scrollHeight, clientHeight } = nav
  const maxScroll = Math.max(0, scrollHeight - clientHeight)
  return {
    scrollTop,
    maxScroll,
    atTop: scrollTop <= 0,
    atBottom: scrollTop + clientHeight >= scrollHeight - 1,
    clientHeight,
  }
}

function scrollDeltaFromKey(key: string, clientHeight: number, shiftKey: boolean): number | null {
  switch (key) {
    case 'ArrowUp':
      return -40
    case 'ArrowDown':
      return 40
    case 'PageUp':
      return shiftKey ? -40 : -clientHeight
    case 'PageDown':
      return shiftKey ? 40 : clientHeight
    case 'Home':
      return Number.NEGATIVE_INFINITY
    case 'End':
      return Number.POSITIVE_INFINITY
    default:
      return null
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * Individual nav item component with prefetch
 *
 * CRITICAL: Must be separate component to follow Rules of Hooks
 * Cannot call hooks inside map/loop - must be at component top level
 */
interface NavItemWithPrefetchProps {
  item: {
    path: string
    label: string
    icon: LucideIcon
    activeModes?: AppMode[]
    featureFlag?: string
  }
  navCollapsed: boolean
  appMode: AppMode
}

function NavItemWithPrefetch({ item, navCollapsed, appMode }: NavItemWithPrefetchProps) {
  // Hook called at component top level (not in loop)
  const prefetchHandlers = useRoutePrefetch(item.path)
  const { getStage } = useFeatureFlags()
  const isActiveInMode = !item.activeModes || item.activeModes.includes(appMode)
  
  // Get stage for this feature (only if it has a feature flag)
  const stage = item.featureFlag ? getStage(item.featureFlag as string) : 'ga'
  const showBadge = !navCollapsed && stage !== 'ga'

  if (item.path === '/resources/docs') {
    // Docs is always GA — never show a lifecycle stage chip for it.
    return (
      <a
        href="/product_docs/"
        target="_blank"
        rel="noopener noreferrer"
        className="nav-item"
        title={navCollapsed ? item.label : undefined}
      >
        <item.icon size={16} />
        {!navCollapsed && <span>{item.label}</span>}
      </a>
    )
  }

  const isExactRoute = item.path === '/' || item.path === '/ontology'

  return (
    <NavLink
      to={item.path}
      className={({ isActive }: { isActive: boolean }) =>
        `nav-item${isActive && isActiveInMode ? ' active' : ''}`
      }
      end={isExactRoute}
      title={navCollapsed ? item.label : undefined}
      {...prefetchHandlers}
    >
      <item.icon size={16} />
      {!navCollapsed && (
        <span className="nav-item__content">
          <span>{item.label}</span>
          {showBadge && <FeatureStageBadge stage={stage} size="sm" />}
        </span>
      )}
    </NavLink>
  )
}

/**
 * Section header component for grouped navigation
 * Always renders same structure - CSS handles collapse/expand visuals
 */
interface NavSectionHeaderProps {
  label: string
}

function NavSectionHeader({ label }: NavSectionHeaderProps) {
  // Always render the same DOM structure to prevent layout shifts
  // CSS handles showing text (expanded) vs divider line (collapsed)
  return (
    <div className="nav-section-header">
      <span>{label}</span>
    </div>
  )
}

export function AppSidebar() {
  const { t } = useTranslation()
  const { mode, toggleMode } = useTheme()
  const { canNavigateTo, isFlagEnabled } = useCanAccess()
  const branding = useBranding()
  // The company_name replaces the "AQUA" wordmark; the "Platform" suffix (Platform
  // mode only) is rendered in the logo gradient so it matches the brand colors.
  // `isResolved` is false until the branding request settles — render the mode
  // label instead of a wordmark until then, so a white-labelled deployment never
  // shows the AQUA fallback while the request is in flight on a slow network.
  const { name: brandText, isResolved: isBrandResolved } = useBrandName()
  // Mode-switch labels come from i18n and carry the {brand} token, so the
  // collapsed button's title/aria-label names the deployment, not the vendor.
  const brandedText = useBrandedText()
  const brandGradient = branding.logo_text_gradient || undefined
  // Shared visibility predicate — the SAME one HomeRoute uses for the post-login
  // landing redirect, so the sidebar and the "/" fallback can never disagree.
  const { isItemVisible } = useNavVisibility()
  const { appMode, isMilkyway, isDataflow, switchMode: switchAppMode } = useAppMode()

  // Build version is VITE_APP_VERSION plus the GitHub Actions run id. It is not
  // part of customer branding, so a blank support_email must not hide it.
  const versionTitle = formatAppVersionTitle(config.app.version)

  // Default to collapsed - expands on hover
  const [navCollapsed, setNavCollapsed] = useState(true)
  const [modeSwitchOpen, setModeSwitchOpen] = useState(false)
  const navSectionRef = useRef<HTMLElement>(null)
  const asideRef = useRef<HTMLElement>(null)
  const isPointerOverSidebarRef = useRef(false)

  // Hover handlers for expand/collapse
  const handleMouseEnter = useCallback(() => {
    isPointerOverSidebarRef.current = true
    setNavCollapsed(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    isPointerOverSidebarRef.current = false
    setNavCollapsed(true)
    setModeSwitchOpen(false)
  }, [])

  // Keep all wheel scroll inside the sidebar — always preventDefault (non-passive native
  // listener) so the main page never scrolls in parallel, including rail padding/edges.
  useEffect(() => {
    const aside = asideRef.current
    if (!aside) return

    const onWheel = (e: WheelEvent) => {
      const nav = navSectionRef.current
      if (!nav) return

      const { scrollTop, maxScroll, atTop, atBottom } = getNavScrollMetrics(nav)

      if (maxScroll <= 0 || (e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
        e.preventDefault()
        return
      }

      nav.scrollTop = Math.max(0, Math.min(maxScroll, scrollTop + e.deltaY))
      e.preventDefault()
    }

    aside.addEventListener('wheel', onWheel, { passive: false })
    return () => aside.removeEventListener('wheel', onWheel)
  }, [])

  // Arrow / Page Up / Page Down / Home / End while the pointer is over the sidebar
  // must scroll the nav list, not the main page (same as wheel containment).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const aside = asideRef.current
      const pointerOrFocusActive =
        isPointerOverSidebarRef.current ||
        Boolean(aside && aside.contains(document.activeElement))
      if (!pointerOrFocusActive) return
      if (isEditableTarget(e.target)) return

      const nav = navSectionRef.current
      if (!nav) return

      const delta = scrollDeltaFromKey(e.key, nav.clientHeight, e.shiftKey)
      if (delta === null) return

      const { scrollTop, maxScroll, atTop, atBottom } = getNavScrollMetrics(nav)

      if (delta === Number.NEGATIVE_INFINITY) {
        if (scrollTop > 0) nav.scrollTop = 0
        e.preventDefault()
        return
      }
      if (delta === Number.POSITIVE_INFINITY) {
        if (scrollTop < maxScroll) nav.scrollTop = maxScroll
        e.preventDefault()
        return
      }

      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        e.preventDefault()
        return
      }

      nav.scrollTop = Math.max(0, Math.min(maxScroll, scrollTop + delta))
      e.preventDefault()
    }

    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [])

  let activeSections
  if (isMilkyway) {
    activeSections = MILKYWAY_NAV_SECTIONS
  } else if (isDataflow) {
    activeSections = DATAFLOW_NAV_SECTIONS
  } else {
    activeSections = NAV_SECTIONS
  }

  let currentModeText: string
  if (appMode === 'milkyway') {
    currentModeText = t('nav.switchToMilkyway')
  } else if (appMode === 'dataflow') {
    currentModeText = t('nav.switchToDataflow')
  } else {
    currentModeText = t('nav.switchToPlatform')
  }
  const currentModeLabel = brandedText(currentModeText)

  const modeOptions: Array<{ mode: 'platform' | 'milkyway' | 'dataflow'; label: string }> = branding.platform_only
    // Platform-only deployments (branding.platform_only) expose no other mode.
    ? [{ mode: 'platform', label: brandedText(t('nav.switchToPlatform')) }]
    : [
        { mode: 'platform', label: brandedText(t('nav.switchToPlatform')) },
        { mode: 'milkyway', label: brandedText(t('nav.switchToMilkyway')) },
        // { mode: 'dataflow', label: brandedText(t('nav.switchToDataflow')) },
      ]

  // Hide the mode switch entirely when there is nothing to switch to (e.g. a
  // Platform-only deployment leaves only the current Platform mode).
  const hasAlternateMode = modeOptions.some((o) => o.mode !== appMode)

  // Process sections - filter items based on access and translate labels
  const processedSections = useMemo(() => {
    const sections = activeSections.map((section: NavSection) => ({
      ...section,
      translatedLabel: section.labelKey ? (t(section.labelKey) as string) : undefined,
      items: section.items.filter(isItemVisible).map((item: NavItem) => ({
          ...item,
          label: t(item.labelKey),
        })),
    })).filter((section: NavSection) => section.items.length > 0)
    return sections
  }, [t, isItemVisible, activeSections])

  let expandedModeLabel: React.ReactNode = currentModeLabel
  if (isBrandResolved && appMode === 'milkyway') {
    expandedModeLabel = <AquaLogo size={13} text={brandText} gradient={brandGradient} />
  } else if (isBrandResolved && appMode === 'platform') {
    expandedModeLabel = <AquaLogo size={13} text={`${brandText} Platform`} gradient={brandGradient} />
  }

  let themeToggleTitle: string | undefined
  if (navCollapsed) {
    themeToggleTitle = mode === 'light' ? t('nav.themeDarkModeTitle') : t('nav.themeLightModeTitle')
  }

  return (
    <aside
      ref={asideRef}
      className={`nav-rail${navCollapsed ? ' collapsed' : ' expanded'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <nav
        ref={navSectionRef}
        className="nav-section"
        style={{
          paddingBottom: modeSwitchOpen && !navCollapsed ? '90px' : '12px',
          transition: 'padding-bottom 0.2s ease',
        }}
      >
        {processedSections.map((section: NavSection) => (
          <div key={section.id} className="nav-section-wrapper">
            {/* Section header (only for sections with labels, not top-level) */}
            {section.translatedLabel && <NavSectionHeader label={section.translatedLabel} />}
            {/* Navigation items */}
            <div className="nav-group-items">
              {section.items.map((item: NavItem) => (
                <NavItemWithPrefetch
                  key={`${section.id}:${item.path}:${item.labelKey}`}
                  item={item}
                  navCollapsed={navCollapsed}
                  appMode={appMode}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
      {/* Mode switch (AQUA / AQUA Platform). Milkyway/AQUA mode is the
          Ideate-focused workspace, so when the user has no access to Ideate
          there is nothing to switch to — hide the entire control. Mirrors the
          same gate the Ideate sidebar item uses (tenant flag + role check). */}
      {isFlagEnabled('ideate_view') && canNavigateTo('ideate') && hasAlternateMode && (
      <div className="nav-mode-switch" style={{ position: 'relative' }}>
        {modeSwitchOpen && !navCollapsed && (
          <div
            className="nav-mode-switch__dropdown"
            onWheel={(e) => {
              if (navSectionRef.current) {
                navSectionRef.current.scrollTop += e.deltaY;
              }
            }}
          >
            {modeOptions
              .filter((o) => o.mode !== appMode)
              .map((o) => {
                let optionLabel: React.ReactNode = o.label
                if (isBrandResolved && o.mode === 'milkyway') {
                  optionLabel = <AquaLogo size={12} text={brandText} gradient={brandGradient} />
                } else if (isBrandResolved && o.mode === 'platform') {
                  optionLabel = <AquaLogo size={12} text={`${brandText} Platform`} gradient={brandGradient} />
                }
                return (
                  <button
                    key={o.mode}
                    type="button"
                    className="nav-mode-switch__dropdown-item"
                    onClick={() => {
                      switchAppMode(o.mode)
                      setModeSwitchOpen(false)
                    }}
                  >
                    <Sparkles size={12} style={{ opacity: 0.7 }} />
                    {optionLabel}
                  </button>
                )
              })}
          </div>
        )}
        <button
          type="button"
          className="nav-mode-switch__btn"
          onClick={() => {
            if (navCollapsed) {
              let nextMode: 'platform' | 'milkyway' | 'dataflow' = 'platform'
              if (appMode === 'platform') {
                nextMode = 'dataflow'
              } else if (appMode === 'dataflow') {
                nextMode = 'milkyway'
              }
              switchAppMode(nextMode)
            } else {
              setModeSwitchOpen(!modeSwitchOpen)
            }
          }}
          title={currentModeLabel}
          aria-label={currentModeLabel}
        >
          <Sparkles size={13} />
          {!navCollapsed && (
            <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {expandedModeLabel}
            </span>
          )}
          {!navCollapsed && (
            <ChevronUp
              size={11}
              className="nav-mode-switch__chevron"
              style={{
                transform: modeSwitchOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          )}
        </button>
      </div>
      )}
      <div className="nav-footer">
        <Link
          to="/help/about"
          className="nav-footer__btn"
          title={navCollapsed ? `About & Support · ${versionTitle}` : undefined}
          aria-label={`About and Support, ${versionTitle}`}
        >
          <HelpCircle size={14} />
          <span className='nav-footer__btn_span'>Help</span>
        </Link>
        {!navCollapsed && (
          <span className="nav-footer__version" title={versionTitle}>
            {versionTitle}
          </span>
        )}

        <button
          type="button"
          className="nav-footer__btn"
          onClick={toggleMode}
          title={themeToggleTitle}
          aria-label={
            mode === 'light' ? t('nav.themeSwitchToDarkAria') : t('nav.themeSwitchToLightAria')
          }
        >
          {mode === 'light' ? <Moon size={14} /> : <Sun size={14} />}
          <span>{mode === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
        </button>
      </div>
    </aside>
  )
}
